import styles from './PowerBIDashboardPage.module.scss';
import { useCallback, useEffect, useState, useRef } from "react";
import { API } from "../../API";
import { Link, useParams } from "react-router-dom";
import { Loader } from "../../components/Loader/Loader";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from '../../contexts/CurrentUser';
import { ampli } from '../../ampli';
import * as pbi from 'powerbi-client';
import { showRequestError } from '../../utils/requestError';

interface PowerBIEmbedConfig {
  report_id: string;
  embed_url: string;
  access_token: string;
}

interface PowerBIReport {
  id: number;
  name: string;
  description: string;
}

export function PowerBIDashboardPage() {
  const { t } = useTranslation();
  const [ currentUser ] = useCurrentUser();
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const [embedConfig, setEmbedConfig] = useState<PowerBIEmbedConfig | null>(null);
  const [report, setReport] = useState<PowerBIReport | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const reportContainer = useRef<HTMLDivElement>(null);

  const fetchEmbedConfig = useCallback((dashboardId: string) => {
    API.fetch('GET', `/API/v2/powerbi-dashboards/${dashboardId}/embed`)
      .then(setEmbedConfig)
      .catch((error) => {
        showRequestError(error, t('Dashboard'));
        setLoadFailed(true);
      });
  }, [t]);

  const fetchReport = useCallback((dashboardId: string) => {
    API.fetch('GET', `/API/v2/powerbi-dashboards/${dashboardId}`)
      .then(setReport)
      .catch((error) => {
        showRequestError(error, t('Dashboard'));
        setLoadFailed(true);
      });
  }, [t]);

  useEffect(() => {
    if (dashboardId) {
      fetchEmbedConfig(dashboardId);
      fetchReport(dashboardId);
    }
  }, [fetchEmbedConfig, fetchReport, dashboardId]);

  useEffect(() => {
    if (embedConfig && report && currentUser) {
      ampli.track({
        event_type: 'Open dashboard',
        event_properties: {
          dashboard_id: report.id,
          user_id: currentUser.id,
          workspace_id: currentUser.workspace.id,
        }
      });
    }
  }, [embedConfig, report, currentUser]);

  useEffect(() => {
    if (embedConfig && reportContainer.current) {
      const powerbi = new pbi.service.Service(
        pbi.factories.hpmFactory,
        pbi.factories.wpmpFactory,
        pbi.factories.routerFactory
      );

      const config: pbi.IEmbedConfiguration = {
        type: 'report',
        tokenType: pbi.models.TokenType.Embed,
        accessToken: embedConfig.access_token,
        embedUrl: embedConfig.embed_url,
        id: embedConfig.report_id,
        permissions: pbi.models.Permissions.Read,
        settings: {
          panes: {
            filters: {
              expanded: false,
              visible: false
            }
          },
          layoutType: pbi.models.LayoutType.Custom,
          customLayout: {
            displayOption: pbi.models.DisplayOption.FitToWidth
          }
        }
      };

      const container = reportContainer.current;
      const embeddedReport = powerbi.embed(container, config) as pbi.Report;

      // Embed tokens expire after ~1h; without an 'error' listener PowerBI
      // dispatches the failure as a DOM CustomEvent that Sentry captures as
      // an unhandled exception (OSAVUL-FRONTEND-E4).
      embeddedReport.on('error', (event) => {
        const error = event.detail as pbi.models.IError;
        if (error?.message === 'TokenExpired' && dashboardId) {
          API.fetch('GET', `/API/v2/powerbi-dashboards/${dashboardId}/embed`)
            .then((data: PowerBIEmbedConfig) => embeddedReport.setAccessToken(data.access_token))
            .catch((refreshError) => showRequestError(refreshError, t('Dashboard')));
        }
      });

      return () => {
        embeddedReport.off('error');
        powerbi.reset(container);
      };
    }
  }, [embedConfig, dashboardId, t]);

  if (loadFailed) {
    return (
      <div className={styles.errorContainer}>
        <p>{t('Something went wrong. Please try again.')}</p>
        <button
          type="button"
          className={styles.retryButton}
          onClick={() => {
            setLoadFailed(false);
            if (dashboardId) {
              fetchEmbedConfig(dashboardId);
              fetchReport(dashboardId);
            }
          }}
        >
          {t('Try again')}
        </button>
      </div>
    );
  }

  if (embedConfig === null || report === null) {
    return <Loader />;
  }

  return (
    <div className={styles.root}>
      <div className="list-content">
        <div className="page-header">
          <div className="breadcrumb">
            <span>
              <Link to="/dashboards">{t('Dashboards')}</Link>
            </span>
            <span>{report.name}</span>
          </div>
        </div>
      </div>
      <div ref={reportContainer} className={styles.reportContainer}></div>
    </div>
  );
}
